/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2021-09-30 00:00:00
 *
 *******************************************************/
//@flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { InlineError, TextStyle } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { AsyncPaginate } from 'react-select-async-paginate';
import { RenderLabelTooltip } from './forms/render-label-tooltip';
import { selectStyle } from './forms/render-select.style';

import * as FilterDataActions from 'actions/filter-data-actions';

// The number of item for first loading
const FIRST_LOADING_COUNT = 500;
// The number of next page
const MAX_NUMBER_ITEMS_API = 30;
// Maximum number of items that system can support
const MAX_INDEX_AGGREGATE = 10000;

type Props = WithI18nProps;

class RenderSelect2API extends Component<Props> {
  state = {
    firstLoad: true,
    skip: 0, // Index for pagination
    skipOfSearch: 0, // Index for pagination of searching text,
    inputValue: ''
  };

  componentWillUnmount() {
    // Reset states
    this.setState({
      skip: 0,
      skipOfSearch: 0,
      inputValue: ''
    });
  }

  render() {
    const {
      name,
      value,
      onChange,
      meta = {},
      options,
      label,
      tooltip,
      isDisabled,
      textHelper,
      placeholderEmpty,
      autoOpen = false,
      i18n
    } = this.props;

    const hasError = !!meta.error && !!meta.touched;
    let { placeholder } = this.props;
    placeholder = _.isEmpty(options)
      ? placeholderEmpty || i18n.translate('AdminHTML.Common.Empty.select_Placeholder')
      : placeholder;
    return (
      <div className={hasError ? 'Polaris-TextField--error' : ''}>
        <RenderLabelTooltip label={label} tooltip={tooltip} />
        <AsyncPaginate
          id={name}
          loadOptions={this.loadOptions}
          value={value}
          onChange={onChange}
          label={label}
          styles={selectStyle}
          placeholder={placeholder}
          maxMenuHeight={150}
          menuPortalTarget={document.body}
          classNamePrefix="BC-Select"
          isDisabled={isDisabled ? true : false}
          noOptionsMessage={() => i18n.translate('AdminHTML.Common.Empty.reactSelect_NoOptions')}
          defaultMenuIsOpen={autoOpen}
          autoFocus={autoOpen}
        />

        {textHelper ? (
          <p style={{ marginTop: '0.25rem' }}>
            <TextStyle variation="subdued">{textHelper}</TextStyle>
          </p>
        ) : (
          ''
        )}
        {meta.error ? <InlineError message={meta.error} /> : ''}
      </div>
    );
  }

  // Wait ms time
  sleep = ms =>
    new Promise(resolve => {
      setTimeout(() => {
        resolve();
      }, ms);
    });

  // Paginate options
  loadOptions = async (inputValue, prevOptions) => {
    const { loadingDataByType } = this.props;

    // Avoid calling multiple time
    if (loadingDataByType) {
      await this.sleep(1000);
    }

    // Filter option list by input text
    if (inputValue) {
      return await this.nextPageWithFilter(inputValue, prevOptions);
    } else {
      // Call API for getting more items
      return await this.nextPage(prevOptions);
    }
  };

  // Get next page
  nextPage = async prevOptions => {
    let { skip, firstLoad } = this.state;

    let data = {
      options: prevOptions,
      hasMore: false
    };

    // Get more data
    skip += firstLoad ? 0 : skip ? MAX_NUMBER_ITEMS_API : FIRST_LOADING_COUNT;
    data = await this.getMore(skip, '', prevOptions);

    // Update state
    this.setState({ skip, firstLoad: false });

    return data;
  };

  // Get next page if having search text
  nextPageWithFilter = async (inputValue, prevOptions) => {
    let { skipOfSearch = 0, inputValue: inputValuePrev } = this.state;

    let data = {
      options: prevOptions,
      hasMore: false
    };

    // Get more data
    if (inputValuePrev !== inputValue) {
      skipOfSearch = 0;
    } else {
      skipOfSearch += skipOfSearch ? MAX_NUMBER_ITEMS_API : FIRST_LOADING_COUNT;
    }
    data = await this.getMore(skipOfSearch, inputValue, prevOptions);

    // Update state
    this.setState({ skip: 0, skipOfSearch, inputValue });

    return data;
  };

  getMore = async (skip, keyword, prevOptions) => {
    const { type = 'tags', getFilterDataByType } = this.props;
    let data = {
      options: [],
      hasMore: false
    };

    // Touch the maximum of query capacity
    if (skip >= MAX_INDEX_AGGREGATE) {
      return data;
    }

    // The index of next pagination is inside the limitation of query
    await getFilterDataByType(type, {
      skip,
      limit: skip ? MAX_NUMBER_ITEMS_API : FIRST_LOADING_COUNT,
      keyword: encodeURIComponent(keyword),
      prefix: ''
    });

    const { filterDataByType = [] } = this.props;

    if (filterDataByType.length > 0) {
      let options = JSON.parse(JSON.stringify(filterDataByType));
      this.removeExcludedValues(options);
      data.options = options;
    }

    // Enable flag having more items
    data.hasMore = filterDataByType.length === (skip ? MAX_NUMBER_ITEMS_API : FIRST_LOADING_COUNT);

    return data;
  };

  removeExcludedValues = options => {
    const { excludedValues = [], value } = this.props;
    if (_.isEmpty(excludedValues)) return;

    excludedValues.forEach(item => {
      _.remove(options, option => {
        return item.value === option.value && item.value !== value.value;
      });
    });
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getFilterDataByType: (type, options) =>
    dispatch(FilterDataActions.getFilterDataByType(type, options, ownProps.i18n))
});

const mapStateToProps = (state, props) => ({
  loadingDataByType: _.get(state.filterData, 'loadingDataByType', false),
  filterDataByType: _.get(state.filterData, 'filterDataByType', [])
});

export const RenderSelect2 = withI18n()(
  connect(mapStateToProps, mapDispatchToProps)(RenderSelect2API)
);
